import { useRef, useState } from "react";
import { UYGUNLUK_ALANLARI, hedefEsit, uygunlukEtiketi, type GelenHedef, type UygunlukKartKalemi, type UygunlukKarti } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  OKUMA_UCU,
  UYGUNLUK_UCU,
  okumaDugmesi,
  okumaEylemleri,
  uygunlukDurumMetni,
  uygunlukEkleyen,
  uygunlukFormu,
  uygunlukKalemMetni,
  uygunlukKarsilastirmaSatirlari,
  uygunlukSayfalari,
  uygunlukTurMetni,
  uygunlukYenidenMetni,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 4 · PR 22: Documents kartının CoC "Document reading" bölümü (prototip belgeler--uygunluk-okuma, kart 1-3; `TeslimOkumaBolumu` iskeleti). Okuma yalnız eşlemenin,
 * kuralların, "Use the reading"in ve "Add to package"in kullandığı değerleri gösterir (sözleşme §14): başlıkta 3, satır başına 3 değer. Tarih, düzenleyen, müşteri, tanım, miktar,
 * uygunluk metni, imzalar ve satış siparişi okunmaz; PN'i stok kataloğunda olmayan kalemler okunmaz. OASIS her kalemin stok paketini PN ve lot ya da seri ile birebir önerir.
 * "Use this place" kartın bugünkü tür+yer kaydını çağırır; "Use the reading" yalnız dosyalama formunu doldurur (sunucuya yazmaz). Dosyalamadan sonra CoC öteki paketlere
 * "Add to package" ile tek tek eklenir; hiçbir paket açılmaz ya da değişmez. `salt` kapanmış satır içindir: form ve karar düğmeleri yok; "Add to package" sunucunun eylemiyle
 * çizilir. Okuma excel'inin adı kartta yoktur.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";

const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);
const durumTonu = (d: "kisi" | "oneri" | "yok") => (d === "kisi" ? "ok" : d === "oneri" ? "info" : "neutral");

export interface UygunlukFormYardimi {
  /** "Use the reading": dosyalama formunun alanları (yalnız kesin CoC no) */
  kullan: (alanlar: Record<string, string>) => void;
  /** "Use the reading": Pages alanı (CoC PDF'in bir kısmıysa) */
  sayfalar?: (s: string) => void;
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
}

export function UygunlukOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: UygunlukKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: UygunlukFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const karsilastirmaSatirlari = uygunlukKarsilastirmaSatirlari(k);
  const yeniden = uygunlukYenidenMetni(k.yenidenKarsilastirma);
  const toplamUyari = (k.karsilastirma?.uyarilar.length ?? 0) + k.kalemler.reduce((t, c) => t + c.uyarilar.length, 0);
  const sayfalar = uygunlukSayfalari(k);
  const gen04 = k.karsilastirma?.uyarilar.find((u) => u.kod === "GEN-04") ?? null;
  const sariGri = toplamUyari > 0 && [...k.kalemler.flatMap((c) => c.uyarilar), ...(k.karsilastirma?.uyarilar ?? [])].every((u) => u.renk !== "kirmizi");
  const dosyalanmis = k.kalemler.some((c) => c.durum === "pakette" || c.durum === "eklenebilir");

  const satirHucresi = (c: UygunlukKartKalemi) => <span className="font-mono">{cevir("{0} · p.{1}", c.sira, c.sayfa ?? "—")}</span>;
  const belgeHucresi = (c: UygunlukKartKalemi) => {
    const suphe = c.degerler.some((d) => d.kesinlik === "suphe");
    return (
      <span className="inline-flex items-center gap-1.5"><Nokta k={suphe ? "suphe" : "kesin"} /><span>{uygunlukKalemMetni(c)}</span>{sayfaLinki(c.sayfa)}</span>
    );
  };
  const paketHucresi = (c: UygunlukKartKalemi) => {
    const p = c.yer.paket;
    if (!p || c.yer.durum === "yok") return <span className="text-ink-3">—</span>;
    return (
      <>
        <span className="mr-1.5">
          {[p.pkg, p.pn, p.iz ? cevir("heat / lot / SN {0}", p.iz) : cevir("no heat / lot / SN"), p.poNo ? cevir("PO {0}", p.poNo) : cevir("no PO")].join(" · ")}
        </span>
        <Etiket tone={durumTonu(c.yer.durum)}>{c.yer.durum === "kisi" ? cevir("your choice") : cevir("proposed")}</Etiket>
        {yardim && c.yer.durum === "oneri" && c.yer.yer && !hedefEsit(c.yer.yer, hedef) && (
          <Buton kucuk className="ml-1 mt-1" disabled={mesgul !== null} mesgul={mesgul === `yer-${c.sira}`} onClick={() => calistir(`yer-${c.sira}`, () => yardim.yerKaydet(c.yer.yer!))}>{cevir("Use this place")}</Buton>
        )}
        {!p.listeVar && <span className="block text-xs text-ink-3">{cevir("no packing list on this package")}</span>}
      </>
    );
  };
  const sonucHucresi = (c: UygunlukKartKalemi) => {
    const ekleyen = uygunlukEkleyen(c);
    return (
      <>
        {c.durum === "eklenebilir" && k.eylemler.ekle
          ? (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === `ekle-${c.sira}`} onClick={() => calistir(`ekle-${c.sira}`, () => api.post(UYGUNLUK_UCU.ekle(satirId), { sira: c.sira }))}>
              {cevir("Add to package")}
            </Buton>
          )
          : uygunlukDurumMetni(c, hedef)}
        {ekleyen && <span className="block text-xs text-ink-3">{cevir("Added by {0} on {1}", ekleyen.kim, tarihSaat(ekleyen.ts))}</span>}
      </>
    );
  };

  return (
    <div data-bolum="uygunluk-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the certificate of conformity for you to check. Nothing is filed or changed.")}</p>}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              o.sayfalar ? cevir("Certificate of conformity p.{0}", aralik(o.sayfalar)) : null,
              k.kod ? cevir("Reading job {0}", k.kod) : null,
              cevir("{0} lines read", o.satirSayisi),
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 space-y-0.5 text-sm" data-bolum="uygunluk-baslik">
            {UYGUNLUK_ALANLARI.map((a) => {
              const d = k.degerler.find((x) => x.alan === a.alan);
              const deger = a.alan === "sertifika_turu" && d?.deger ? uygunlukTurMetni(d.deger) : d?.deger;
              return (
                <li key={a.alan} className="flex gap-2">
                  <Nokta k={d?.kesinlik ?? "bos"} />
                  <span className="w-[10rem] shrink-0 text-ink-2">{cevir(uygunlukEtiketi(a.alan))}</span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                    {sayfaLinki(d?.sayfa ?? null)}
                    {d?.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                  </span>
                </li>
              );
            })}
          </ul>

          <AltBaslik>{cevir("Lines of the CoC")}</AltBaslik>
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" data-bolum="bl-kars">
              <thead><tr className="border-b border-line">
                <th className={TABLO_BASI}>{cevir("Line")}</th>
                <th className={TABLO_BASI}>{cevir("On the CoC")}</th>
                <th className={TABLO_BASI}>{cevir("Stock package")}</th>
                <th className={TABLO_BASI}>{cevir("Result")}</th>
              </tr></thead>
              <tbody className="divide-y divide-line">
                {k.kalemler.map((c) => (
                  <tr key={c.sira} data-kalem={c.sira}>
                    <td className={TABLO_HUCRESI}>{satirHucresi(c)}</td>
                    <td className={TABLO_HUCRESI}>{belgeHucresi(c)}</td>
                    <td className={TABLO_HUCRESI}>{paketHucresi(c)}</td>
                    <td className={TABLO_HUCRESI}>{sonucHucresi(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-1 text-xs text-ink-3">{cevir("OASIS proposes the package of the same part number whose heat / lot / SN holds the lot or a serial number of the line. Nothing is filed by itself.")}</p>
          {(k.eylemler.ekle || dosyalanmis) && (
            <p className="mt-1 text-xs text-ink-3">
              {cevir("“Add to package” files this PDF on the package of that line too (Certificate of conformity (CoC), Doc no. {0}). One line at a time; a package or a document already in Registers does not change. A line without a proposed package is filed on Registers by hand.", uygunlukFormu(k).belgeNo || "—")}
            </p>
          )}

          {yardim && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-bolum="okumayi-kullan">
              <Buton kucuk disabled={mesgul !== null} onClick={() => {
                yardim.kullan(uygunlukFormu(k));
                if (sayfalar && yardim.sayfalar) yardim.sayfalar(sayfalar);
              }}>{cevir("Use the reading")}</Buton>
              <span className="w-full text-xs text-ink-3">
                {cevir("“Use the reading” fills Doc no. with the certain CoC number and Pages with the pages of the CoC when it is part of the PDF; an uncertain or empty value stays empty for you to type. Nothing is filed until you confirm.")}
              </span>
            </div>
          )}
          {yardim && (gen04 || sariGri) && (
            <Band tip="not" className="mt-2">
              <span className="block font-medium">{cevir("Before you file")}</span>
              {gen04 && <span className="block">{cevir("The certificate reads as {0}. Change “What is this file?” to {0} before filing, or file it as it is.", cevir(gen04.degerler[1] ?? ""))}</span>}
              {sariGri && <span className="block">{cevir("Amber and grey alerts do not stop filing; they stay with the reading.")}</span>}
            </Band>
          )}
          <p className="mt-1 text-xs text-ink-3">
            {cevir("Not read: date, issuer, customer, description and quantity of the items, the conformity statement, signatures and the sales order. Items whose part number is not in the stock catalogue are not read.")}
          </p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {(karsilastirmaSatirlari.length > 0 || yeniden) && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {karsilastirmaSatirlari.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.karsilastirildi ? "kesin" : n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
              {yeniden && (
                <li className="flex gap-2">
                  <Nokta k="kesin" />
                  <span className="min-w-0">{yeniden}</span>
                </li>
              )}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <div className="space-y-2">
                  {k.kalemler.filter((c) => c.uyarilar.length > 0).map((c) => (
                    <div key={c.sira} data-kalem-uyari={c.sira}>
                      <p className="text-xs font-medium text-ink-2">{cevir("Line {0}: {1}", c.sira, c.pn ?? "—")}</p>
                      <ul className="mt-0.5 space-y-1.5">
                        {c.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                      </ul>
                    </div>
                  ))}
                  {k.karsilastirma.uyarilar.length > 0 && (
                    <ul className="space-y-1.5">
                      {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                    </ul>
                  )}
                </div>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(UYGUNLUK_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(UYGUNLUK_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(UYGUNLUK_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
